import {
  DashboardContext,
  useDashboardContext,
} from '@/contexts/DashboardContext.tsx';
import { useDynamicPageContext } from '@/contexts/DynamicPageContext';
import { UserContext } from '@/contexts/UserContext.tsx';
import { DynamicPageVersionDetailsForm } from '@/modals/PublishDynamicPageVersionDrawer.tsx';
import {
  getDynamicPageVersionContents,
  getDynamicPageVersions,
} from '@/services/DynamicPagesService.ts';
import {
  DynamicPageVersion,
  DynamicPageVersionMetadata,
  WithId,
} from '@superfunnel/types';
import Loader from '@superfunnel/ui-core/Loader';
import Badge from '@superfunnel/ui-core/v2/Badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@superfunnel/ui-core/v2/DropdownMenu';
import { PopoverContent } from '@superfunnel/ui-core/v2/popover';
import { useVirtualizer } from '@tanstack/react-virtual';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  Copy,
  LongArrowRightUp,
  MoreVert,
  NavArrowLeft,
  Trash,
  Xmark,
} from 'iconoir-react';
import { useContext, useEffect, useRef, useState } from 'react';

dayjs.extend(relativeTime);

const PAGE_SIZE = 50;

export const VersionsHistoryPopover = ({
  close,
  open,
}: {
  close: () => void;
  open: boolean;
}) => {
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage } = useDynamicPageContext();

  const [versions, setVersions] =
    useState<WithId<DynamicPageVersionMetadata>[]>();
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);

  const [selectedVersionMetadata, setSelectedVersionMetadata] =
    useState<WithId<DynamicPageVersionMetadata>>();
  const [selectedVersionFull, setSelectedVersionFull] =
    useState<DynamicPageVersion>();
  const [isLoadingVersionDetails, setIsLoadingVersionDetails] = useState(false);

  const [error, setError] = useState<Error>();

  // Fetch full version details when a version is selected
  useEffect(() => {
    if (!selectedVersionMetadata) {
      setSelectedVersionFull(undefined);
      return;
    }
    if (!selectedAccount) return;
    if (!selectedDynamicPage) return;

    setIsLoadingVersionDetails(true);
    let relevant = true;

    getDynamicPageVersionContents(
      selectedAccount.id,
      selectedDynamicPage.id,
      selectedVersionMetadata.id,
    )
      .then((fullVersion) => {
        if (!relevant) return;
        setSelectedVersionFull(fullVersion);
      })
      .catch((e) => {
        if (!relevant) return;
        console.error('Failed to load version details:', e);
        setError(e);
      })
      .finally(() => {
        if (!relevant) return;
        setIsLoadingVersionDetails(false);
      });

    return () => {
      relevant = false;
    };
  }, [selectedVersionMetadata, selectedAccount, selectedDynamicPage]);

  useEffect(() => {
    setError(undefined);
    setVersions(undefined);

    if (!open) return;
    if (!selectedAccount) return;
    if (!selectedDynamicPage) return;

    setIsLoadingVersions(true);

    let relevant = true;
    getDynamicPageVersions(
      selectedAccount.id,
      selectedDynamicPage.id,
      PAGE_SIZE,
    )
      .then((versionDocs) => {
        if (!relevant) return;
        setVersions(versionDocs.map((doc) => ({ ...doc.data(), id: doc.id })));
      })
      .finally(() => {
        if (!relevant) return;
        setIsLoadingVersions(false);
      });

    return () => {
      relevant = false;
    };
  }, [open, selectedAccount, selectedDynamicPage]);

  return (
    <PopoverContent
      className="flex h-[376px] w-80 flex-col overflow-hidden rounded-[20px] px-2 pt-1 pb-2 lg:w-100"
      align="end"
      side="bottom"
      sideOffset={4}
      alignOffset={-5}
    >
      <div className="mb-1 flex items-center justify-between px-2 py-1.5">
        <div className={'flex items-center gap-2'}>
          {selectedVersionMetadata && (
            <div className="flex h-7 items-center">
              <button onClick={() => setSelectedVersionMetadata(undefined)}>
                <NavArrowLeft className={'size-4'} />
              </button>
            </div>
          )}
          <h4 className="text-base-900 font-inter text-base font-medium -tracking-[0.01em]">
            Versions history
          </h4>
        </div>

        <button type={'button'} onClick={() => close()}>
          <Xmark className={'size-5'} />
        </button>
      </div>
      <form className="flex grow flex-col overflow-y-auto">
        {/* Divider container */}
        {!selectedVersionMetadata && (
          <>
            {/* Versions list */}
            <div className="relative flex h-full flex-col">
              {isLoadingVersions && (
                <div className="flex h-32 items-center justify-center">
                  <Loader />
                </div>
              )}
              {!isLoadingVersions && (
                <div className="relative block w-full flex-1">
                  <VersionsHistoryList
                    versions={versions ?? []}
                    onSelect={(version) => {
                      setSelectedVersionMetadata(version);
                    }}
                  />
                </div>
              )}

              {error && (
                <div className="flex h-32 items-center justify-center">
                  <p className="text-sm text-red-500">{error.message}</p>
                </div>
              )}
            </div>
          </>
        )}

        {selectedVersionMetadata && (
          <DynamicPageVersionDetailsForm
            versionName={selectedVersionMetadata.name}
            versionDescription={selectedVersionMetadata.description}
            readOnly
            changes={selectedVersionFull?.changes || []}
            hasPrevVersion={
              (versions?.indexOf(selectedVersionMetadata) ?? 0) + 1 <
              (versions?.length ?? 0)
            }
            loading={isLoadingVersionDetails}
            disabled={false}
          />
        )}
      </form>
    </PopoverContent>
  );
};
interface VersionsHistoryListProps {
  versions: WithId<DynamicPageVersionMetadata>[];
  onSelect: (version: WithId<DynamicPageVersionMetadata>) => void;
}

const VersionsHistoryList = ({
  versions,
  onSelect,
}: VersionsHistoryListProps) => {
  const versionsListContainerRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: (versions?.length ?? 0) + 1,
    estimateSize: () => 80, // estimate row height for accurate scrollbar dragging
    getScrollElement: () => versionsListContainerRef.current,
  });

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div ref={versionsListContainerRef} className="h-full w-full">
      <div
        style={{
          height: virtualizer.getTotalSize(),
          width: '100%',
          position: 'relative',
        }}
      >
        <div className={'font-inter space-y-1'}>
          {virtualItems.map((virtualRow) => (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
            >
              {virtualRow.index === versions.length ? (
                <CreationHistoryItem />
              ) : (
                <VersionsHistoryItem
                  version={versions[virtualRow.index]}
                  index={virtualRow.index}
                  onSelect={onSelect}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

interface VersionsHistoryItemProps {
  version: WithId<DynamicPageVersionMetadata>;
  index: number;
  onSelect: (version: WithId<DynamicPageVersionMetadata>) => void;
}

const VersionsHistoryItem = ({
  version,
  index,
  onSelect,
}: VersionsHistoryItemProps) => {
  const { teamMembers } = useContext(DashboardContext);

  return (
    <div
      key={version.id}
      className="bg-base-100 cursor-pointer rounded-lg px-2.5 py-2 transition"
      onClick={() => onSelect(version)}
    >
      <div className="flex items-start justify-between gap-3 -tracking-[0.01em] lg:gap-6">
        <div>
          <p className="text-base-900 text-sm font-medium">{version.name}</p>
          {version.description && (
            <p className="line-clamp-2 text-xs">{version.description}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {index === 0 && (
            <Badge className={'px-1.5 py-0.5 text-sm'} variant={'success'}>
              Current
            </Badge>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger
              className={
                'flex size-6 items-center justify-center rounded-lg hover:bg-white'
              }
            >
              <MoreVert className={'size-4'} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled>
                <LongArrowRightUp className={'text-base-500'} />
                Restore this version (coming soon)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <p className="text-base-500 mt-0.5 line-clamp-2 flex items-center gap-1.5 text-xs">
        <span>
          {teamMembers?.find((t) => t.id === version.createdBy)?.data().email ??
            'Unknown'}
        </span>
        •
        <span>
          {'toDate' in version.createdAt
            ? dayjs(version.createdAt.toDate()).fromNow()
            : ''}
        </span>
      </p>
    </div>
  );
};

const CreationHistoryItem = () => {
  const { teamMembers } = useDashboardContext();
  const { selectedDynamicPage } = useDynamicPageContext();

  return (
    <div
      key={selectedDynamicPage?.id}
      className="bg-base-100 rounded-lg px-2.5 py-2 transition"
    >
      <div className="flex items-start justify-between gap-3 -tracking-[0.01em] lg:gap-6">
        <div>
          <p className="text-base-900 text-sm font-medium">
            {selectedDynamicPage?.data().name}
          </p>
          {selectedDynamicPage?.data().description && (
            <p className="line-clamp-2 text-xs">
              {selectedDynamicPage?.data().description}
            </p>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger
            className={
              'flex size-6 items-center justify-center rounded-lg hover:bg-white'
            }
          >
            <MoreVert className={'size-4'} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem>
              <LongArrowRightUp className={'text-base-500'} />
              Restore this version
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Copy className={'text-base-500'} />
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuItem className={'text-error-600'}>
              <Trash />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <p className="text-base-500 mt-0.5 line-clamp-2 flex items-center gap-1.5 text-xs">
        <span>
          {teamMembers
            ?.find((t) => t.id === selectedDynamicPage?.data().createdBy)
            ?.data().email ?? 'Unknown'}
        </span>
        •
        <span>
          {selectedDynamicPage &&
          'toDate' in selectedDynamicPage.data().createdAt
            ? dayjs(selectedDynamicPage?.data().createdAt.toDate()).fromNow()
            : ''}
        </span>
      </p>
    </div>
  );
};
